import {
  effect,
  signal,
  inject,
  OnInit,
  Injector,
  computed,
  Component,
  ViewChild,
  DestroyRef,
  AfterViewInit,
  ViewContainerRef,
  ChangeDetectionStrategy,
} from '@angular/core';

import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { LoggerService } from '@services/logger.service';
import { faWandMagic } from '@fortawesome/free-solid-svg-icons';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  DialogCloseReason,
  DialogCloseOptions,
  SuiDialogTabsComponent,
  SuiDialogRightHeaderSlot,
  SuiDialogRightContentSlot,
  SuiDialogRightFooterSlot,
} from '@sui/dialog';

import { ToastService } from '@services/toast.service';
import { UtilsService } from 'src/app/core/services/utils.service';
import { FeedPersistenceService } from 'src/app/core/services/feed-persistence.service';
import { SidebarService } from 'src/app/core/services/sidebar.service';
import {
  FeedApiService,
  QueryValidationError,
  QueryValidationResponse,
} from 'src/app/api/services/feed-api.service';

import { TabDialogBase } from 'src/app/shared/abstract/tab-dialog-base';

import { injectAvailableFeedTypesQuery } from 'src/app/api/queries/user.queries';

import { SearchBuilderComponent } from '../../../shared/components/search-builder/search-builder.component';
import { ObjectPermissionsComponent } from '../../../objects/components/object-permissions/object-permissions.component';
import { FeedTypeSelectorComponent } from '../../components/feed-type-selector/feed-type-selector.component';
import { QueryEditorGeneralSettingsComponent } from '../../../shared/components/query-editor-general-settings/query-editor-general-settings.component';

import { ButtonConfig } from 'src/app/admin/admin.models';
import { NomObjectClass, NomObjectTypes } from 'src/app/core/constants';

import {
  createFeedSuccess,
  createFeedFail,
} from '../../../core/state/feed.actions';
import { submitFormFailure } from '../../../core/state/form.actions';

import { AppState } from 'src/app/core/state/app.state';
import { selectCreatingFeed, selectUser } from 'src/app/core/state';

import { take, tap } from 'rxjs';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { SuiButtonComponent } from '@sui/button';
import { WizardStepsComponent } from '../../../shared/components/wizard-steps/wizard-steps.component';
import { NgClass } from '@angular/common';

interface DialogData {}
interface DialogOutput {}

@Component({
  selector: 'app-feed-create-wizard-dialog',
  templateUrl: './feed-create-wizard-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './feed-create-wizard-dialog.component.scss',
  imports: [
    SuiDialogTabsComponent,
    SuiDialogRightHeaderSlot,
    FaIconComponent,
    SuiButtonComponent,
    SuiDialogRightContentSlot,
    WizardStepsComponent,
    NgClass,
    SuiDialogRightFooterSlot,
  ],
})
export class FeedCreateWizardDialogComponent
  extends TabDialogBase<DialogData, DialogOutput>
  implements OnInit, AfterViewInit
{
  // TODO: Migrate to Simple Dialog with content component which handles the tab logic

  protected destroyRef = inject(DestroyRef);
  private store = inject(Store<AppState>);
  private actions$ = inject(Actions);
  private feedApi = inject(FeedApiService);
  private feedPersistence = inject(FeedPersistenceService);
  private sidebarService = inject(SidebarService);
  private logger = inject(LoggerService);
  private utilsService = inject(UtilsService);

  @ViewChild('tabHost', { read: ViewContainerRef })
  protected override tabHost!: ViewContainerRef;
  private toastService = inject(ToastService);
  private injector = inject(Injector);

  // * User Feed Types query - Start
  private userId = computed(() => {
    const user = this.store.selectSignal(selectUser)();
    return user?.id ?? '';
  });

  availableFeedTypesQuery = injectAvailableFeedTypesQuery(this.userId());
  feedTypesData = computed(() => this.availableFeedTypesQuery.data() ?? []);
  isFeedTypesLoading = computed(() => this.availableFeedTypesQuery.isLoading());
  isFeedTypesError = computed(() => this.availableFeedTypesQuery.isError());
  // * User Feed Types query - End

  currentStep: number = 0;
  combinedFeedIds: Set<string> = new Set();
  objectTypeId = signal<number>(NomObjectTypes.ONLINE_FEED);
  private isCreating = false;

  // ! check if selected feed type has available quota
  isSelectedFeedTypeAvailable = computed(() => {
    const feedTypes = this.feedTypesData();
    const typeId = this.objectTypeId();
    const selected = feedTypes.find((ft) => ft.type_id === typeId);
    const result = selected ? (selected.left ?? 0) > 0 : false;

    return result;
  });

  protected buttons: ButtonConfig[] = [
    {
      variant: 'primary',
      text: this.translateService.instant(
        'feed-create-wizard-popup.button.previous',
      ),
      leadingIcon: ['fas', 'arrow-left'],
      action: () => this.previousStep(),
      disabled: true,
    },
    {
      variant: 'primary',
      text: this.translateService.instant(
        'feed-create-wizard-popup.button.next',
      ),
      leadingIcon: ['fas', 'arrow-right'],
      action: () => this.nextStep(),
    },
  ];
  wizardSteps = [
    'Choose feed type',
    'Define search',
    'Define Name and Workspace',
    'Share with team',
  ];

  objectNameTitle = '';
  objectIcon = faWandMagic;

  ngOnInit(): void {
    this.setupUIState();
    this.setupTabs();

    // Close sidebar to prevent double query hints overlay
    this.sidebarService.closeSidebar();

    this.store
      .select(selectCreatingFeed)
      .pipe(
        tap((creatingFeed) => {
          this.isCreating = creatingFeed;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    // * Handle Feed Types query error and update Button State when data loads
    effect(
      () => {
        if (this.isFeedTypesError()) {
          this.toastService.showError(
            this.translateService.instant(
              'create-feed-wizard.feed-type-tile.error-message',
            ),
          );
        }

        this.updateNavigationButtons();
      },
      { injector: this.injector },
    );
  }

  ngAfterViewInit(): void {
    this.initializeBase();

    const activeTabId = this.tabManager.activeTabId();
    const component = this.tabManager.getTabComponent(activeTabId);
    if ('feedTypeIdChange' in component) {
      component.feedTypeIdChange
        .pipe(
          tap((typeId: NomObjectTypes) => {
            this.handleFeedTypeChange(typeId);
          }),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
    }
  }

  setupUIState(): void {
    this.objectNameTitle = this.translateService.instant(
      'feed-create-wizard-popup.title.create-new-data-feed',
    );
  }

  setupTabs(): void {
    this.tabs = [
      {
        id: 'feed-type',
        label: this.translateService.instant(
          'feed-create-wizard-popup.tab.choose-feed-type',
        ),
        component: FeedTypeSelectorComponent,
        inputs: {},
      },
      {
        id: 'define-search',
        label: this.translateService.instant(
          'feed-create-wizard-popup.tab.define-search',
        ),
        component: SearchBuilderComponent,
        inputs: {
          objectTypeId: this.objectTypeId(),
          showPreviewPane: true,
        },
      },
      {
        id: 'add-filters',
        label: this.translateService.instant(
          'feed-create-wizard-popup.tab.add-filters',
        ),
        component: QueryEditorGeneralSettingsComponent,
        inputs: {
          objectTypeId: this.objectTypeId(),
        },
      },
      {
        id: 'set-permissions',
        label: this.translateService.instant(
          'feed-create-wizard-popup.tab.set-permissions',
        ),
        component: ObjectPermissionsComponent,
        inputs: {
          selectorType: 'users,groups',
          objectClassId: NomObjectClass.FEED,
        },
      },
    ];
  }

  isValidationError(
    response: QueryValidationResponse,
  ): response is QueryValidationError {
    return 'error' in response && response.error === true;
  }

  nextStep(): void {
    const isLastStep = this.currentStep === this.tabs.length - 1;
    const currentFormGroup = this.tabManager.getTabFormGroup(
      this.tabs[this.currentStep].id,
    );

    if (!currentFormGroup) {
      this.logger.error(
        'No form group found for current step',
        this.currentStep,
      );
      return;
    }

    this.tabManager.markActiveFormGroupAsTouched(currentFormGroup);

    if (
      currentFormGroup.status !== 'VALID' &&
      currentFormGroup.status !== 'DISABLED'
    ) {
      return;
    }

    const queryCreatedFromControl = currentFormGroup.get('query_created_from');
    const queryTextControl = currentFormGroup.get('query_text');

    if (
      queryCreatedFromControl &&
      queryCreatedFromControl.value === 'editor' &&
      queryTextControl?.value
    ) {
      const query = queryTextControl.value;
      const objectTypeId = this.objectTypeId();
      const component = this.tabManager.getTabComponent(
        this.tabs[this.currentStep].id,
      ) as SearchBuilderComponent;

      this.feedApi.validateQuery(objectTypeId, query, 'editor').subscribe({
        next: (response: QueryValidationResponse) => {
          if (this.isValidationError(response)) {
            component?.onSubmitError({
              type: 'validation',
              line: response['response'].line,
              message: response['response'].message,
            });

            // Do not proceed to next step on validation error
            return;
          } else {
            component?.onSubmitSuccess({});
          }

          // Only advance if query is valid
          this.advanceStepAfterValidation(isLastStep);
        },
        error: (err) => {
          console.error('Validation failed:', err);
          queryTextControl.setErrors({ invalidQuery: true });

          component?.onSubmitError({
            type: 'validation',
            line: err['response'].line,
            message: err['response'].message,
            column: err['response'].column,
          });
          // Do not proceed
        },
      });

      return; // Don't advance step now, wait for async validation
    }

    // No async validation needed, proceed directly
    this.advanceStepAfterValidation(isLastStep);
  }

  private advanceStepAfterValidation(isLastStep: boolean): void {
    if (isLastStep) {
      if (this.isCreating) return;
      this.buttons[1].disabled = true;
      this.buttons[1].text = this.translateService.instant(
        'feed-create-wizard-popup.button.creating',
      );
      this.buttons[1].loading = true;
      this.save();
      return;
    }

    const next = this.getNextStep(this.currentStep);
    this.currentStep = next;
    this.tabManager.selectTab(this.tabs[this.currentStep].id);
    this.updateNavigationButtons();
  }
  private getNextStep(current: number): number {
    // Add your custom skipping logic here
    if (
      current === 0 &&
      (this.objectTypeId() === NomObjectTypes.CLIPPING_FEED ||
        this.objectTypeId() === NomObjectTypes.STORY_FEED)
    ) {
      return current + 2; // skip step 1
    }
    return current + 1;
  }

  private getPreviousStep(current: number): number {
    // Add your custom back-step logic here
    if (
      current === 2 &&
      (this.objectTypeId() === NomObjectTypes.CLIPPING_FEED ||
        this.objectTypeId() === NomObjectTypes.STORY_FEED)
    ) {
      return current - 2; // skip back from step 2 to 0
    }
    return current - 1;
  }

  private updateNavigationButtons(): void {
    const isFirst = this.currentStep === 0;
    const isLast = this.currentStep === this.tabs.length - 1;
    const isAvailable = this.isSelectedFeedTypeAvailable();

    this.buttons[0].disabled = isFirst;

    // ! disable "Next" button on first step if selected Feed Type limit is reached (0)
    if (isFirst) {
      this.buttons[1].disabled = !isAvailable;
    } else {
      this.buttons[1].disabled = false;
    }

    if (isLast) {
      this.buttons[1].text = this.translateService.instant('btn.confirm');
      this.buttons[1].leadingIcon = ['fas', 'check'];
      this.buttons[1].variant = 'primary';
    } else {
      this.buttons[1].text = this.translateService.instant(
        'feed-create-wizard-popup.button.next',
      );
      this.buttons[1].leadingIcon = ['fas', 'arrow-right'];
      this.buttons[1].variant = 'primary';
    }

    this.cdr.markForCheck();
  }

  previousStep(): void {
    if (this.currentStep === 0) return;

    const prev = this.getPreviousStep(this.currentStep);
    this.currentStep = prev;
    this.tabManager.selectTab(this.tabs[this.currentStep].id);

    this.updateNavigationButtons();
  }

  handleFeedTypeChange(data: NomObjectTypes) {
    this.objectTypeId.set(data);

    this.tabs[1].inputs['objectTypeId'] = data;
    this.tabs[2].inputs['objectTypeId'] = data;

    const t = (key: string) => this.translateService.instant(key);

    const steps = {
      chooseType: t('feed-create-wizard-popup.tab.choose-feed-type'),
      defineSearch: t('feed-create-wizard-popup.tab.define-search'),
      defineName: t('feed-create-wizard-popup.tab.define-name-and-workspace'),
      shareWithTeam: t('feed-create-wizard-popup.tab.share-with-team'),
      selectFeedsToCombine: t(
        'feed-create-wizard-popup.tab.select-feeds-to-combine',
      ),
    };

    if (data === NomObjectTypes.COMBINED_FEED) {
      this.tabs[1].component = SearchBuilderComponent;
      this.tabs[1].inputs['objectTypeIds'] = [
        NomObjectTypes.ONLINE_FEED,
        NomObjectTypes.STORY_FEED,
        NomObjectTypes.SOCIAL_FEED,
        NomObjectTypes.CLIPPING_FEED,
        NomObjectTypes.BROADCAST_FEED,
      ];
      this.wizardSteps = [
        steps.chooseType,
        steps.selectFeedsToCombine,
        steps.defineName,
        steps.shareWithTeam,
      ];
    } else if (
      data === NomObjectTypes.CLIPPING_FEED ||
      data === NomObjectTypes.STORY_FEED
    ) {
      this.wizardSteps = [
        steps.chooseType,
        steps.defineName,
        steps.shareWithTeam,
      ];
    } else {
      this.tabs[1].component = SearchBuilderComponent;
      this.wizardSteps = [
        steps.chooseType,
        steps.defineSearch,
        steps.defineSearch,
        steps.shareWithTeam,
      ];
    }
    this.tabManager.reloadTab('define-search');

    // * уpdate Button State when Feed Type changes
    this.updateNavigationButtons();
  }

  createFeed() {
    this.tabManager.markAllAsTouched();
    const invalidFormGroups = this.tabManager.getInvalidTabIds();

    if (invalidFormGroups.length === 0) {
      // * dotted i18n control names (title_i18n.language_id_N) must become a
      // * nested title_i18n object before hitting the wire - PHP mangles dots
      // * in param names; same conversion as QueryEditorDialog createFeed()
      let allFormData = this.utilsService.convertFormDataI18nKeys(
        this.tabManager.combineValues(),
      );

      if (this.objectTypeId() == NomObjectTypes.COMBINED_FEED) {
        allFormData = {
          ...allFormData,
          params: {
            combined_feeds: allFormData['combined_feeds'],
          },
        };
      }
      if (this.objectTypeId() == NomObjectTypes.SOCIAL_FEED) {
        const socialSources = allFormData['post_social_source_id'];
        allFormData = {
          ...allFormData,
          params: {
            social_sources: socialSources,
          },
        };
      }
      // Starter widgets picked on step 3 ride along as `widget_selections:
      // WidgetSelection[]` (see QueryEditorGeneralSettingsComponent). Surfaced
      // here so the payload contract is visible at the dispatch site.
      const widgetSelections = allFormData['widget_selections'] ?? [];

      allFormData = {
        ...allFormData,
        object_type_id: this.objectTypeId(),
        query:
          this.objectTypeId() === NomObjectTypes.STORY_FEED
            ? '*'
            : allFormData['query_text'],
        widget_selections: widgetSelections,
      };
      this.logger.debug('Feed data:', allFormData);
      this.feedPersistence.create(allFormData);
    } else {
      this.logger.debug('Invalid form groups:', invalidFormGroups);
    }
  }

  protected override performSave(
    formData: unknown,
  ): DialogCloseOptions<DialogOutput> {
    this.createFeed();
    return { reason: DialogCloseReason.Save, data: formData };
  }

  save(): void {
    const formData = this.tabManager.combineValues();
    const closeOptions = this.performSave(formData);

    // Wait for the create-feed result before closing — on success we close
    // immediately, on failure we keep the dialog open and restore the
    // button state so the user can fix the input and retry.
    this.actions$
      .pipe(
        ofType(createFeedSuccess, createFeedFail, submitFormFailure),
        take(1),
        tap((action) => {
          if (action.type === createFeedSuccess.type) {
            this.dirtyHandler.closeImmediately(closeOptions);
            return;
          }
          this.resetConfirmButton();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private resetConfirmButton(): void {
    this.buttons[1].disabled = false;
    this.buttons[1].loading = false;
    this.buttons[1].text = this.translateService.instant('btn.confirm');
    this.cdr.markForCheck();
  }

  async cancel(): Promise<void> {
    await this.dirtyHandler.tryClose({
      reason: DialogCloseReason.Cancel,
    });
  }
}
